import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { normalizeDestinationName, type PassportEntryRow } from '@/lib/passport';

const isDuplicateError = (err: unknown): boolean =>
  typeof err === 'object' && err !== null && 'code' in err && (err as { code?: string }).code === '23505';

// A foreign-key violation specifically on user_id means the session's user no longer
// exists in auth.users (e.g. the account was removed, or -- in local dev -- the
// database was reset out from under an already-open session). RLS alone can't catch
// this: WITH CHECK only compares the token's claimed user_id to itself, so it passes
// before the FK constraint (correctly) rejects the insert. Surfacing the raw Postgres
// message here ("violates foreign key constraint...") would confuse a normal user, so
// this is treated as an invalid session and handled by signing out, not as a generic
// failure.
const isStaleSessionError = (err: unknown): boolean =>
  typeof err === 'object' &&
  err !== null &&
  'code' in err &&
  (err as { code?: string }).code === '23503' &&
  'message' in err &&
  typeof (err as { message?: unknown }).message === 'string' &&
  (err as { message: string }).message.includes('surf_passport_entries_user_id_fkey');

export const usePassportEntries = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['passport-entries', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('surf_passport_entries')
        .select(
          `id, user_id, country_id, destination_name, destination_name_normalized, month,
           linked_destination_id, created_at, updated_at,
           countries:countries(id, name, iso2),
           destinations:linked_destination_id(hero_image_url)`
        )
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as PassportEntryRow[];
    },
  });
};

export interface PassportEligibleCountry {
  id: string;
  name: string;
  iso2: string | null;
}

export const usePassportEligibleCountries = () => {
  return useQuery({
    queryKey: ['passport-eligible-countries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('countries')
        .select('id, name, iso2')
        .eq('is_surf_passport_eligible', true)
        .order('name');
      if (error) throw error;
      return (data ?? []) as PassportEligibleCountry[];
    },
    staleTime: 60 * 60 * 1000,
  });
};

interface PassportEntryInput {
  countryId: string;
  destinationName: string;
  month: number;
}

export const useAddPassportEntry = () => {
  const { user, signOut } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ countryId, destinationName, month }: PassportEntryInput) => {
      if (!user) throw new Error('You need to sign in first.');
      const { error } = await supabase.from('surf_passport_entries').insert({
        user_id: user.id,
        country_id: countryId,
        destination_name: destinationName.trim(),
        destination_name_normalized: normalizeDestinationName(destinationName),
        month,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['passport-entries', user?.id] });
      toast({ title: 'Added to your Passport' });
    },
    onError: (err) => {
      if (isDuplicateError(err)) {
        // Neutral acknowledgement, not an error -- this only fires on a literal
        // re-submission (same country + exact text + month), an accidental
        // double-submit guard, not destination-spelling validation. No destructive
        // styling: it shouldn't read as a rejection.
        toast({ title: "You've already got this one." });
      } else if (isStaleSessionError(err)) {
        toast({
          title: 'Your session has expired',
          description: 'Please sign in again to continue.',
          variant: 'destructive',
        });
        void signOut();
      } else {
        toast({ title: 'Something went wrong', description: (err as Error).message, variant: 'destructive' });
      }
    },
  });
};

export const useUpdatePassportEntry = () => {
  const { user, signOut } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, countryId, destinationName, month }: PassportEntryInput & { id: string }) => {
      const { error } = await supabase
        .from('surf_passport_entries')
        .update({
          country_id: countryId,
          destination_name: destinationName.trim(),
          destination_name_normalized: normalizeDestinationName(destinationName),
          month,
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['passport-entries', user?.id] });
      toast({ title: 'Changes saved' });
    },
    onError: (err) => {
      if (isDuplicateError(err)) {
        // Neutral acknowledgement, not an error -- this only fires on a literal
        // re-submission (same country + exact text + month), an accidental
        // double-submit guard, not destination-spelling validation. No destructive
        // styling: it shouldn't read as a rejection.
        toast({ title: "You've already got this one." });
      } else if (isStaleSessionError(err)) {
        toast({
          title: 'Your session has expired',
          description: 'Please sign in again to continue.',
          variant: 'destructive',
        });
        void signOut();
      } else {
        toast({ title: 'Something went wrong', description: (err as Error).message, variant: 'destructive' });
      }
    },
  });
};

export const useDeletePassportEntry = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('surf_passport_entries').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['passport-entries', user?.id] });
      toast({ title: 'Removed from your Passport' });
    },
    onError: (err: Error) => {
      toast({ title: 'Something went wrong', description: err.message, variant: 'destructive' });
    },
  });
};
